import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper, Alert } from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { getPlanDetails, STRIPE_PUBLIC_KEY } from '../../../services/stripeService';
import { useTranslation } from "react-i18next";
import axiosInstance from '../../../useApi';

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

const Checkout = () => {
  const { t } = useTranslation();
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [stripe, setStripe] = useState(null);

  useEffect(() => {
    const planDetails = getPlanDetails(planId);
    
    if (!planDetails) {
      setError(t('Plan not found. Please select a valid plan.'));
      setLoading(false);
      return;
    }
    
    setPlan(planDetails);
    setLoading(false);

    const initStripe = async () => {
      const stripeInstance = await stripePromise;
      setStripe(stripeInstance);
    };
    
    initStripe();
  }, [planId, t]);

  const handlePaymentSuccess = async () => {
    if (!stripe || !plan) return;
    
    try {
      setProcessing(true);
      
      const response = await axiosInstance.post('/paiement/create-checkout-session/', {
        product_id: plan.priceId,
        metadata: {
          productId: plan.priceId,
        }
      });
      
      const { sessionId } = response.data;
      
      if (!sessionId) {
        throw new Error('Impossible de créer la session de paiement');
      }
      
      const result = await stripe.redirectToCheckout({ sessionId });
      
      if (result.error) {
        throw new Error(result.error.message);
      }
      
    } catch (err) {
      console.error('Erreur lors de la création de la session de paiement:', err);
      setError(t('An error occurred while processing your payment. Please try again later.'));
      setProcessing(false);
    }
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage || t('An error occurred while processing your payment. Please try again later.'));
  };

  const handleCancel = () => {
    navigate('/dashboard/extrapages/pricing-plans');
  };

  if (loading || processing) {
    return (
      <div id='content-page' className='content-page'>
        <Container>
          <Row className="justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <Col md={6} className="text-center">
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">{t("Loading...")}</span>
              </Spinner>
              <Typography variant="h6" className="mt-3">
                {processing ? t('Processing your payment...') : t('Preparing your payment...')}
              </Typography>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div id='content-page' className='content-page'>
        <Container>
          <Row className="justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <Col md={6} className="text-center">
              <Alert severity="success" sx={{ mb: 3 }}>
                {t("Payment successful! You will be redirected...")}
              </Alert>
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">{t("Redirection...")}</span>
              </Spinner>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div id='content-page' className='content-page'>
        <Container>
          <Row className="justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <Col md={6}>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth 
                onClick={handleCancel}
              >
                Retour aux forfaits
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div id='content-page' className='content-page'>
      <Container>
        <Row>
          <Col sm="12">
            <Card className="position-relative inner-page-bg bg-primary" style={{ height: "150px" }}>
              <div className="inner-page-title">
                <h3 className="text-white">{t('Checkout')}</h3>
                <p className="text-white">{t('Complete your subscription payment')}</p>
              </div>
            </Card>
          </Col>
          
          <Col sm="12" className="mt-4">
            <Box sx={{ maxWidth: 600, mx: 'auto', p: { xs: 2, md: 3 }, pt: 4 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">{t('Loading...')}</span>
                  </Spinner>
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 4 }}>
                  {error}
                </Alert>
              ) : paymentSuccess ? (
                <Box sx={{ textAlign: 'center', my: 5 }}>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    {t('Payment processed successfully!')}
                  </Alert>
                  <Typography variant="body1">
                    {t('Redirecting to confirmation page...')}
                  </Typography>
                </Box>
              ) : (
                <Paper elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {plan.name} {t('Plan')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {plan.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', my: 2 }}>
                      <Typography variant="h3" component="span">
                        {plan.price}€
                      </Typography>
                      <Typography variant="subtitle1" component="span" sx={{ ml: 1 }}>
                        /{t(planId.includes('annual') ? 'year' : 'month')}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {processing ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <Spinner animation="border" role="status" variant="primary">
                        <span className="visually-hidden">{t('Processing payment...')}</span>
                      </Spinner>
                    </Box>
                  ) : (
                    <Button 
                      variant="contained" 
                      fullWidth
                      disabled={!stripe}
                      onClick={handlePaymentSuccess}
                      sx={{ mt: 2, py: 1.5 }}
                    >
                      {t("Proceed to payment")} - {plan.price}€ {planId.includes('annual') ? `/${t('year')}` : `/${t('month')}`}
                    </Button>
                  )}
                  
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button 
                      variant="text" 
                      color="primary"
                      onClick={handleCancel}
                      disabled={processing}
                    >
                      {t('Cancel')}
                    </Button>
                  </Box>
                </Paper>
              )}
            </Box>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Checkout;
